import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Divider, IconButton, InputBase, Paper, Typography } from '@mui/material';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Container } from "reactstrap";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PaginationTableWithFuncBtn from '../../../components/Util/PaginationTableWithFuncBtn';
import SearchIcon from '@mui/icons-material/Search';
import toastAlert from '../../../js/Base/ToastAlert';
import DeleteModal from '../../../components/Util/DeleteModal';
import CreateOrgModal from './CreateOrgModal';
import UpdateOrgDrawer from './UpdateOrgDrawer';

import {
  getAllCompGroupsByCompNo,
  getCompanyGroupsByNoList,
  getCompanyMembersByNoList,
  EditCompanyGroup,
} from '../../../api/companyApi';
import { getAccountsByNoList } from '../../../api/accountApi';

export default function CompanyOrgListBlock() {
  const [loading, setLoading] = useState(false);

  const [isUpdateDrawerOpen, setIsUpdateDrawerOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreateOrgModalOpen, setIsCreateOrgModalOpen] = useState(false);

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [inputValue, setInputValue] = useState('');

  const [compGroups, setCompGroups] = useState([]);
  const [tempGroups, setTempGroups] = useState([]);

  const getCompGroupsByWebApi = async () => {
    const companyUuid = localStorage.getItem("company_uuid");
    if (!companyUuid) {
      toastAlert.autoClose(true).showError("查無資料，請聯絡系統管理員");
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      // 取得公司群組
      const groupRes = await getAllCompGroupsByCompNo(companyUuid);
      const compGroupList = groupRes.data?.groups || [];

      setCompGroups(compGroupList);
      // 取得 parent 群組
      const parentNoList = compGroupList.map((group) => group.parent_uuid);
      let companyGroupRes = [];
      if (parentNoList.length > 0) {
        const res = await getCompanyGroupsByNoList(parentNoList);
        companyGroupRes = res.data?.groups || [];
      }

      // 建立 parent map
      const companyGroupMap = {};
      companyGroupRes.forEach(parent => {
        companyGroupMap[parent.uuid] = parent;
      });

      // 建立 tempGroups，即使 parent_groups_data 為 null 也可執行
      const newTempGroups = compGroupList.map(group => ({
        ...group,
        parent_groups_data: companyGroupMap[group.parent_uuid] || null
      }));

      setTempGroups(newTempGroups);
      setLoading(false);
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    }
  };

  useEffect(() => {
    const fetchMembersAndAccounts = async () => {
      if (tempGroups.length > 0) {
        const managerUuidList = tempGroups.map((group) => group.manager_uuid);

        // 用 manager_uuid 查詢 member，建立 memberMap
        const comp_member = await getCompanyMembersByNoList(managerUuidList);
        
        const compMemberList = comp_member.data?.members || [];
        if (compMemberList.length > 0) {
          const memberMap = {};
          compMemberList.forEach((m) => {
            memberMap[m.id] = m;
            if (m.uuid) memberMap[m.uuid] = m;
          });

          // 將manager_data加進group
          const groupsWithManager = tempGroups.map(group => ({
            ...group,
            manager_data: memberMap[group.manager_uuid] || null
          }));

          // 收集acct_uuid，查詢account資料，建立accountMap
          const acctUuidList = groupsWithManager
            .map((group) => group.manager_data?.acct_uuid)
            .filter(Boolean);

          if (acctUuidList.length > 0) {
            const manager_acct_list = await getAccountsByNoList(acctUuidList);
            const compManagerList = manager_acct_list.data?.accounts || [];

            if (compManagerList.length > 0) {
              const accountMap = {};
              compManagerList.forEach((acc) => {
                accountMap[acc.uuid] = acc;
              });
          
              // 將account加進manager_data
              const finalGroups = groupsWithManager.map((group) => {
                if (!group.manager_data) return group;
          
                return {
                  ...group,
                  manager_data: {
                    ...group.manager_data,
                    account: accountMap[group.manager_data.acct_uuid] || null
                  }
                };
              });

              setGroups(finalGroups);
            };
          }
        }
      }
    };
  
    fetchMembersAndAccounts();
  }, [tempGroups]);


  const handleEdit = (id) => {
    handleUpdateDrawerOpen(id);
  };

  const handleUpdateDrawerOpen = (selectedGroupNo) => {
    const group = groups.find(g => g.uuid === selectedGroupNo);
    setSelectedGroup(group || null);
    setIsUpdateDrawerOpen(true);
  };

  const handleUpdateDrawerClose = () => {
    setIsUpdateDrawerOpen(false);
    setSelectedGroup(null);
  };


  useEffect(() => {
    getCompGroupsByWebApi();
  }, []);

  const handleOpenCreateOrg = async () => {
    if (compGroups.length === 0 && !loading) {
      await getCompGroupsByWebApi();
    }
    setIsCreateOrgModalOpen(true);
  };

  const handleOrgModalClose = () => {
    setIsCreateOrgModalOpen(false);
  };


  const handleDelete = (id) => {
    handleDeleteModalOpen(id)
  };

  const handleDeleteModalOpen = (selectedGroupNo) => {
    const group = groups.find(g => g.uuid === selectedGroupNo);
    setSelectedGroup(group || null);
    setDeleteModalOpen(true);
  };
  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setSelectedGroup(null);
  };


  const handleDeleteConfirm = async () => {
    if (!selectedGroup) return;
    try {
      const res = await EditCompanyGroup({
        group_uuid: selectedGroup.uuid,
        status: 0,
      });
      const data = res.data;
      if(data.success) {
        toastAlert.autoClose(true).showSuccess("刪除成功");
      } else {
        showErrorMessage(data?.code);
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("刪除失敗，請稍後再試");
    } finally {
      handleDeleteModalClose();
      getCompGroupsByWebApi();
    }
  };

  const showErrorMessage = (code) => {
    switch (code) {
      case "1":
        toastAlert.autoClose(true).showError("該部門存在子部門，必須先刪除子部門");
        break;
      case "2":
        toastAlert.autoClose(true).showError("最上層部門已經存在");
        break;
      case "3":
        toastAlert.autoClose(true).showError("最上層部門不可設定上層部門");
        break;
      default:
        toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
        break;
    }
  };

  const col_name = [
    "組織名稱", "父組織名稱", "功能",
  ];

  const dataFrame = [
    { keyName: 'name' },
    { keyName: 'parent_groups_data.name' },
    { keyName: 'func', isClick: true, editBtn: true, delBtn: true },
  ];

  const col_size = ['35%', '45%', '20%'];

  return (
    <React.Fragment>
      <Container className="mt-2">
        <Breadcrumbs
          className="link-area"
          style={{ padding: "0 0 20px 0", fontWeight: 'bold'}}
          separator={<NavigateNextIcon style={{ fontSize: '20px', fontWeight: 'bold' }} />}
          aria-label="breadcrumb"
        >
          <Link to="/admin-management" style={{ color: '#1d1d1d', fontSize: '20px', textDecoration: 'none', fontWeight: 'bold' }}>
            <p style={{ marginBottom: '0px' }}>公司管理</p>
          </Link>
          <Link to="/company/company-org-management" style={{ color: '#1d1d1d', fontSize: '20px', textDecoration: 'none', fontWeight: 'bold' }}>
            <p style={{ marginBottom: '0px' }}>組織管理</p>
          </Link>
        </Breadcrumbs>

        <Card style={{ marginBottom: '50px'}}>
          <Card.Header style={{ backgroundColor: 'transparent' }}>
            <strong style={{ fontSize:'16px', fontWeight: 'bold', fontFamily: 'Arial, sans-serif' }}>公司組織管理</strong>
          </Card.Header>

        <Card.Body className="CompOrgManagement ListBlock">
          <Row>
            <Col md="4">
              <Paper variant="outlined" component="form" sx={{ p: '2px 2px', display: 'flex', alignItems: 'left', height: '32px' }}>
                <InputBase
                  sx={{ flex: 1, p: '10px' }}
                  style={{ fontSize: '12px', boxShadow: 'none' }}
                  placeholder="請輸入組織名稱"
                  inputProps={{ 'aria-label': 'search keyword' }}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
                <IconButton type="button" sx={{ p: '12px' }} aria-label="search">
                  <SearchIcon style={{ color: '#E2E2E2' }} />
                </IconButton>
              </Paper>
            </Col>
            <Col md="6" />
            <Col md="2" className="d-flex justify-content-end align-items-center">
              <Button
                className="btn-secondary btn-org"
                onClick={handleOpenCreateOrg}
                >
                新增組織
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="d-flex justify-content-center my-3">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <div className="mt-3">
              <PaginationTableWithFuncBtn
                rowData={tempGroups.filter(
                  (m) => m.name.indexOf(inputValue) > -1 || m.parent_groups_data?.name?.indexOf(inputValue) > -1
                )}
                colName={col_name}
                dataFrame={dataFrame}
                col_size={col_size}
                delFunc={handleDelete}
                editModalOpen={handleEdit}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      <DeleteModal
        isModalOpen={deleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleUpdate={handleDeleteConfirm}
        title={'刪除組織'}
        content={`請確認是否刪除此組織：${selectedGroup?.name || ''}`}
      />

      {/* {isModalOpen && (
        <EditProjectModal
          isCreate={selectedProject === null}
          project={selectedProject}
          companyMembers={companyMembers}
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          handleClose={handleEditModalClose}
          submitEffect={getProjectsByWebApiTest}
        />
      )}  */}
      <CreateOrgModal
        groupsList={compGroups}
        isModalOpen={isCreateOrgModalOpen}
        handleClose={handleOrgModalClose}
        submitEffect={getCompGroupsByWebApi}
      />
      <UpdateOrgDrawer
        isModalOpen={isUpdateDrawerOpen}
        handleClose={handleUpdateDrawerClose}
        groupsList={compGroups}
        selectedGroup={selectedGroup}
        submitEffect={getCompGroupsByWebApi}
      />
      </Container>
    </React.Fragment>
  );
}
